import { bnPageHead } from "@/lib/bn-head";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Section } from "@/components/Section";
import { Reveal } from "@/components/Reveal";
import { JsonLd } from "@/components/JsonLd";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { ArrowUpRight } from "lucide-react";
import { SITE } from "@/content/site";
import { BN_CASES } from "@/content/bn/cases";

const TITLE = "এসইও কাজের উদাহরণ ও ব্যবহারিক পদ্ধতি";
const DESC =
  "ওয়েবসাইট, ক্যাম্পেইন ও কীওয়ার্ড গবেষণার উদাহরণ এবং এসইওর ব্যবহারিক পদ্ধতি — প্রতিটি পেজে প্রমাণের সীমা উল্লেখ করা আছে।";

export const Route = createFileRoute("/bn/case-studies/")({
  head: () =>
    bnPageHead({
      path: "/bn/case-studies",
      enPath: "/case-studies",
      title: TITLE,
      desc: DESC,
      keywords: ["এসইও কেস স্টাডি", "এসইও ফলাফল", "এসইও পোর্টফোলিও বাংলাদেশ"],
    }),
  component: BnCaseStudiesIndex,
});

function BnCaseStudiesIndex() {
  return (
    <>
      <Breadcrumbs items={[{ name: "হোম", path: "/bn" }, { name: "কেস স্টাডি", path: "/bn/case-studies" }]} />
      <h1 className="sr-only">এসইও কেস স্টাডি ও ফলাফল</h1>
      <Section eyebrow="কেস স্টাডি" title={<>কাজের <span className="text-gradient">উদাহরণ।</span></>}>
        <p className="max-w-2xl text-lg text-muted-foreground -mt-8">
          কাজের উপকরণ ও উদাহরণভিত্তিক নির্দেশনা। আগের অপ্রমাণিত ফলাফলের দাবি প্রত্যাহার করা হয়েছে;
          এগুলোকে যাচাইকৃত ট্রাফিক, র‍্যাঙ্কিং বা ROI-এর প্রমাণ হিসেবে উপস্থাপন করা হচ্ছে না।
        </p>
      </Section>

      <div className="container-x grid md:grid-cols-2 gap-6 pb-32">
        {BN_CASES.map((c, i) => (
          <Reveal key={c.slug} delay={(i % 2) * 0.1}>
            <Link
              to="/bn/case-studies/$slug"
              params={{ slug: c.slug }}
              className="group block rounded-3xl border border-border bg-card p-10 hover:border-primary/40 hover:shadow-[var(--shadow-glow)] transition-all h-full"
            >
              <div className="flex items-start justify-between">
                <span className="text-6xl">{c.cover}</span>
                <ArrowUpRight size={20} className="text-muted-foreground group-hover:text-primary group-hover:-translate-y-1 group-hover:translate-x-1 transition" />
              </div>
              <div className="mt-10 text-xs uppercase tracking-[0.18em] text-muted-foreground">
                {c.region} · {c.industry}
              </div>
              <h2 className="mt-3 font-display text-3xl leading-snug">{c.headline}</h2>
              <p className="mt-4 text-muted-foreground">{c.summary}</p>
            </Link>
          </Reveal>
        ))}
      </div>

      <JsonLd
        data={[
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "@id": `${SITE.url}/bn/case-studies#collection`,
            url: `${SITE.url}/bn/case-studies`,
            name: TITLE,
            description: DESC,
            inLanguage: "bn",
            isPartOf: { "@id": `${SITE.url}/#website` },
            mainEntity: {
              "@type": "ItemList",
              itemListElement: BN_CASES.map((c, i) => ({
                "@type": "ListItem",
                position: i + 1,
                name: c.headline,
                url: `${SITE.url}/bn/case-studies/${c.slug}`,
              })),
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "হোম", item: `${SITE.url}/bn` },
              { "@type": "ListItem", position: 2, name: "কেস স্টাডি", item: `${SITE.url}/bn/case-studies` },
            ],
          },
        ]}
      />
    </>
  );
}
